/**
 * useTaskDetails - Hook para detalhes completos de uma tarefa
 * 
 * Chama RPC get_task_details e gerencia mutations de update/status/delete.
 * 
 * @version 1.1.0
 * RELEASE: 2026-08-21 v1.1.0 - Tipagem de retention_details e legal_details
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger para depuração
const logger = createLogger('useTaskDetails');

/**
 * Tipo completo da tarefa retornada por get_task_details
 */
export interface TaskDetail {
  id: string;
  tenant_id: string;
  number: number;
  title: string;
  description: string | null;
  description_html: string | null;
  category_id: string;
  category_name: string;
  category_color: string;
  classification_id: string | null;
  classification_name: string | null;
  classification_color: string | null;
  phase_id: string;
  phase_name: string;
  phase_color: string;
  phase_is_final: boolean;
  /** v1.9.0: Tipo da tarefa (normal | retention | legal) retornado por get_task_details */
  task_type: 'normal' | 'retention' | 'legal';
  /** v1.1.0: Campos específicos do módulo Retenção (JSONB da RPC get_task_details) */
  retention_details?: Record<string, any> | null;
  /** v1.1.0: Campos específicos do módulo Jurídico (JSONB da RPC get_task_details) */
  legal_details?: Record<string, any> | null;
  priority: string;
  status: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_to_avatar: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_by_avatar: string | null;
  parent_task_id: string | null;
  parent_task_title: string | null;
  parent_task_number: number | null;
  due_date: string | null;
  started_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  /** v1.8.0: Data/hora em que a tarefa entrou na fase atual (mantido por trigger trg_tasks_phase_updated_at) */
  phase_updated_at: string | null;
  subtasks_count: number;
  comments_count: number;
  attachments_count: number;
  /** Total de cobranças financeiras vinculadas à tarefa (badge da aba Financeiro) */
  charges_count?: number;
  contact_id: string | null;
  contact_name: string | null;
  deal_id: string | null;
  deal_title: string | null;
  phase_sort_order: number;
  total_category_phases: number;
  customer_id: string | null;
  /** v1.5.0: Se o usuário pode visualizar a tarefa pai */
  can_view_parent: boolean;
  /** v1.7.0: Contagem de pausas */
  pauses_count: number;
  /** v1.7.0: Tempo total de pausa em segundos */
  total_pause_duration_seconds: number;
  /** v1.7.0: ID da pausa ativa (null se não pausada) */
  current_pause_id: string | null;
  /** v1.7.0: Início da pausa ativa (null se não pausada) */
  current_pause_started_at: string | null;
}

/**
 * Tipo para comentário de tarefa
 */
export interface TaskComment {
  id: string;
  content: string;
  content_html: string | null;
  user_id: string;
  user_name: string | null;
  user_avatar: string | null;
  created_at: string;
  updated_at: string;
  source: string;
  replies_count: number;
}

/**
 * Tipo para subtarefa ativa (bloqueante)
 */
export interface ActiveSubtask {
  id: string;
  number: number;
  title: string;
  status: string;
}

/**
 * Hook para detalhes de uma tarefa
 */
export function useTaskDetails(taskId: string | null) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Query principal
  const detailsQuery = useQuery({
    queryKey: ['task-details', taskId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_task_details', {
        p_task_id: taskId!,
        p_tenant_hint: currentTenant?.id || null,
      });

      if (error) {
        logger.error('Erro ao buscar detalhes da tarefa', { error: error.message });
        throw error;
      }

      if (!data || (data as any[]).length === 0) {
        throw new Error('Tarefa não encontrada');
      }

      return (data as any[])[0] as TaskDetail;
    },
    enabled: !!taskId && !!currentTenant?.id,
  });

  // Query de comentários com polling de backup (15s) enquanto modal aberto
  const commentsQuery = useQuery({
    queryKey: ['task-comments', taskId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_task_comments', {
        p_task_id: taskId!,
        p_tenant_hint: currentTenant?.id || null,
      });

      if (error) {
        logger.error('Erro ao buscar comentários', { error: error.message });
        throw error;
      }

      return (data || []) as TaskComment[];
    },
    enabled: !!taskId && !!currentTenant?.id,
    // Polling de 15s como backup caso Realtime falhe
    refetchInterval: taskId ? 15000 : false,
  });

  // Mutation para atualizar tarefa
  const updateMutation = useMutation({
    mutationFn: async (params: Record<string, any>) => {
      const { error } = await supabase.rpc('update_task', {
        p_id: taskId!,
        ...params,
        p_tenant_hint: currentTenant?.id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-details', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task-comments', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
      toast.success('Tarefa atualizada');
    },
    onError: (err: Error) => toast.error(`Erro: ${err.message}`),
  });

  // Mutation para alterar status
  // v1.5.0: aceita objeto { status, phaseId } para ajustar a fase na MESMA transação
  // v1.6.0: aceita motivo/descrição da conclusão (Retenção e Jurídico), gravados
  //         na mesma transação da RPC change_task_status
  const changeStatusMutation = useMutation({
    mutationFn: async (
      input:
        | string
        | {
            status: string;
            phaseId?: string | null;
            completionReasonId?: string | null;
            completionNote?: string | null;
          },
    ) => {
      const newStatus = typeof input === 'string' ? input : input.status;
      const phaseId = typeof input === 'string' ? null : (input.phaseId ?? null);
      const completionReasonId =
        typeof input === 'string' ? null : (input.completionReasonId ?? null);
      const completionNote =
        typeof input === 'string' ? null : (input.completionNote ?? null);
      const { error } = await supabase.rpc('change_task_status', {
        p_task_id: taskId!,
        p_new_status: newStatus as any,
        p_phase_id: phaseId,
        p_completion_reason_id: completionReasonId,
        p_completion_note: completionNote,
        p_tenant_hint: currentTenant?.id || null,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-details', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task-comments', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
      toast.success('Status atualizado');
    },
    onError: (err: Error) => toast.error(`Erro: ${err.message}`),
  });

  // Mutation para deletar tarefa
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('delete_task', {
        p_id: taskId!,
        p_tenant_hint: currentTenant?.id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
      toast.success('Tarefa excluída');
    },
    onError: (err: Error) => toast.error(`Erro: ${err.message}`),
  });

  // Mutation para adicionar comentário
  const addCommentMutation = useMutation({
    // RELEASE: 2026-09-12 - aceita mentionedUserIds para criar as menções de
    // forma atômica junto com o andamento (RPC create_task_comment v1.2.0)
    mutationFn: async ({ content, contentHtml, mentionedUserIds }: { content: string; contentHtml?: string; mentionedUserIds?: string[] }): Promise<string> => {
      const { data, error } = await supabase.rpc('create_task_comment', {
        p_task_id: taskId!,
        p_content: content,
        p_content_html: contentHtml || null,
        p_mentioned_user_ids: mentionedUserIds?.length ? mentionedUserIds : null,
        p_tenant_hint: currentTenant?.id || null,
      } as never);
      if (error) throw error;
      return data as string; // Retorna o ID do comentário criado
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-comments', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task-details', taskId] });
      toast.success('Andamento adicionado');
    },
    onError: (err: Error) => toast.error(`Erro: ${err.message}`),
  });

  // Mutation para editar comentário (autor ou admin)
  const updateCommentMutation = useMutation({
    mutationFn: async ({ commentId, content, contentHtml }: { commentId: string; content: string; contentHtml?: string }) => {
      const { error } = await supabase.rpc('update_task_comment', {
        p_id: commentId,
        p_content: content,
        p_content_html: contentHtml || null,
        p_tenant_hint: currentTenant?.id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-comments', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task-details', taskId] });
      toast.success('Andamento atualizado');
    },
    onError: (err: Error) => toast.error(`Erro: ${err.message}`),
  });

  // Mutation para deletar comentário
  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase.rpc('delete_task_comment', {
        p_id: commentId,
        p_tenant_hint: currentTenant?.id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-comments', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task-details', taskId] });
      toast.success('Andamento removido');
    },
    onError: (err: Error) => toast.error(`Erro: ${err.message}`),
  });

  /**
   * Verifica subtarefas bloqueantes para a ação solicitada na tarefa pai.
   * - 'complete' bloqueia filhas em pending/in_progress/paused
   * - 'cancel' / 'delete' bloqueiam qualquer filha com status != cancelled
   * Default = 'complete' para retrocompatibilidade.
   */
  const checkActiveSubtasks = async (
    action: 'complete' | 'cancel' | 'delete' = 'complete'
  ): Promise<ActiveSubtask[]> => {
    if (!taskId || !currentTenant?.id) return [];

    const { data, error } = await supabase.rpc('get_active_subtasks', {
      p_task_id: taskId,
      p_tenant_hint: currentTenant.id,
      p_action: action,
    });

    if (error) {
      logger.error('Erro ao verificar subtarefas ativas', { error: error.message, action });
      return [];
    }

    return (data || []) as ActiveSubtask[];
  };


  return {
    task: detailsQuery.data || null,
    isLoading: detailsQuery.isLoading,
    error: detailsQuery.error,
    comments: commentsQuery.data || [],
    isLoadingComments: commentsQuery.isLoading,
    updateTask: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    changeStatus: changeStatusMutation.mutateAsync,
    isChangingStatus: changeStatusMutation.isPending,
    deleteTask: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    addComment: addCommentMutation.mutateAsync,
    isAddingComment: addCommentMutation.isPending,
    updateComment: updateCommentMutation.mutateAsync,
    isUpdatingComment: updateCommentMutation.isPending,
    deleteComment: deleteCommentMutation.mutateAsync,
    checkActiveSubtasks,
  };
}
